import React from 'react'
import classNames from 'classnames'
import { AchieveLinkNoMui as AchieveLink } from 'components/AchieveLinkNoMui/AchieveLinkNoMui'
import { LINK_UI_COMPONENTS } from 'constants/contentfulUiComponentTypes'
import { useConfig } from 'providers/AnalyticsProvider'
import auth0SignUp from 'utils/auth0SignUp/auth0SignUp'
import { TypographyNoMui } from 'components/Contentful/TypographyNoMui'

// Variants for styling
const LINK_BUTTON_VARIANTS = {
  TEXT: 'text',
  OUTLINED: 'outlined',
  CONTAINED: 'contained',
}

// Variant + color → Tailwind class map (matches Ascend Button tokens)
// contained + primary:   bg #3300FF, hover #154199, text #F8F9FC (white)
// contained + secondary: bg #EFF5FF, hover blue[100], text blue[600]
// outlined:              bg transparent, hover blue[50], text #3300FF, border #3300FF
// text:                  bg transparent, hover #EFF5FF, text #3300FF
// default (no variant):  light blue CTA style
const VARIANT_STYLES = {
  'contained-primary': 'bg-[#3300FF] hover:bg-[#154199] text-[#F8F9FC]',
  'contained-secondary': 'bg-[#EFF5FF] hover:bg-[#D2E2FE] text-[#154199]',
  'contained-error': 'bg-[#CF264E] hover:bg-[#A01E3E] text-[#F8F9FC]',
  outlined:
    'bg-transparent hover:bg-[#EFF5FF] text-[#3300FF] border-2 border-solid border-[#3300FF]',
  text: 'bg-transparent hover:bg-[#EFF5FF] text-[#3300FF]',
  default: 'bg-[#EFF5FF] hover:bg-[#D2E2FE]',
}

function resolveVariantClasses(variant, color) {
  if (variant === 'contained' && color) {
    return VARIANT_STYLES[`contained-${color}`] || VARIANT_STYLES['contained-primary']
  }
  if (variant === 'outlined') return VARIANT_STYLES.outlined
  if (variant === 'text') return VARIANT_STYLES.text
  return VARIANT_STYLES.default
}

const ButtonNoMui = React.forwardRef(
  (
    {
      onClick,
      passThroughOnClick = () => {},
      label,
      variant = LINK_BUTTON_VARIANTS.CONTAINED,
      color = 'primary',
      className = '',
      disabled = false,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      typographicOptions,
      fullWidth,
      ...restProps
    },
    ref
  ) => {
    const localClickHandler = (event) => {
      if (typeof onClick === 'function') {
        onClick?.(event)
      }
      passThroughOnClick?.(event)
    }

    return (
      <button
        ref={ref}
        type="button"
        data-variant={variant}
        className={classNames(
          resolveVariantClasses(variant, color),
          'rounded-[8px]',
          variant !== 'outlined' && 'border-0',
          'font-[inherit]',
          'font-bold',
          'text-[18px]',
          'py-2',
          'px-6',
          'leading-[120%]',
          'min-h-12',
          'hover:cursor-pointer',
          'transition duration-250 ease-in-out',
          'hover:shadow-md',
          'disabled:bg-[#EFF2F8] disabled:text-[#C0CBD8] disabled:cursor-not-allowed disabled:shadow-none',
          fullWidth && 'w-full',
          className
        )}
        onClick={localClickHandler}
        disabled={disabled}
        {...restProps}
      >
        <TypographyNoMui
          className={classNames('m-0', 'leading-[120%]', 'tracking-[0.023em]')}
          content={label}
        />
      </button>
    )
  }
)

export const LinkButtonNoMui = ({
  content,
  onClick,
  variant,
  color,
  typographicOptions,
  track,
  disabled = false,
  noLink = false,
  target = '',
  ...restProps
}) => {
  const { config = {} } = useConfig()
  if (!content?.fields && !content) return null

  const { uiComponent, linkText, linkHref } = content?.fields ? content.fields : content

  // Handle LinkAuth0 use case
  if (uiComponent === LINK_UI_COMPONENTS.LINK_AUTH0) {
    noLink = true
    onClick = () => auth0SignUp(config)
  }

  // For disabled or noLink states, render as button element (no href)
  if (disabled || noLink) {
    return (
      <ButtonNoMui
        label={linkText}
        passThroughOnClick={onClick}
        variant={variant}
        color={color}
        typographicOptions={typographicOptions}
        disabled={disabled}
        {...restProps}
      />
    )
  }

  return (
    <AchieveLink
      href={linkHref}
      passHref
      track={track}
      noLink={noLink}
      withNextLink
      disabled={disabled}
      target={target}
    >
      <ButtonNoMui
        label={linkText}
        passThroughOnClick={onClick}
        variant={variant}
        color={color}
        typographicOptions={typographicOptions}
        disabled={disabled}
        {...restProps}
      />
    </AchieveLink>
  )
}

LinkButtonNoMui.VARIANTS = LINK_BUTTON_VARIANTS

ButtonNoMui.displayName = 'LinkButtonNoMui'
